import { Database, Model, Q } from '@nozbe/watermelondb';
import { isEmpty } from 'lodash';

import { ContactPayload } from '../../../../types/Contacts';
import { LocationModel } from '../../../../types/Location';
import { ValidationOperation } from '../../../validation/validator';

export type ContactEntityTableName =
    | 'contacts'
    | 'locations'
    | 'horse_contacts'
    | 'event_contacts'
    | 'event_contact_reminders'
    | 'users';

export type PreparedContactData = {
    record: Model;
    table: ContactEntityTableName;
    operation: ValidationOperation;
};

export const mapRecord = (
    record: Model,
    table: ContactEntityTableName,
    operation: ValidationOperation,
): PreparedContactData => ({
    record,
    table,
    operation,
});

export const mapRecords = (
    records: Model[],
    table: ContactEntityTableName,
    operation: ValidationOperation,
): PreparedContactData[] =>
    records.map((record) => mapRecord(record, table, operation));

export const addPreparedDataBatch = async (
    preparedData: PreparedContactData[],
    database: Database,
) => {
    await database.batch(...preparedData.map(({ record }) => record));
};

export const isPreparedCreate = (record: Model): boolean => {
    return (
        (record as unknown as { _preparedState?: string })._preparedState ===
        'create'
    );
};

export const prepareContactLocation = async ({
    location,
    userId,
    organisationID,
    database,
    preparedLocationsByPlaceId,
}: {
    location: ContactPayload['location'];
    userId: string;
    organisationID: string;
    database: Database;
    preparedLocationsByPlaceId?: Map<string, LocationModel>;
}): Promise<{
    preparedLocation?: LocationModel;
    existingLocation?: LocationModel;
}> => {
    if (!location || isEmpty(location)) return {};

    const preparedLocation = preparedLocationsByPlaceId?.get(location.placeId);

    if (preparedLocation) return { preparedLocation };

    const locationCollection =
        database.collections.get<LocationModel>('locations');

    const existingLocations = await locationCollection
        .query(Q.where('place_id', location.placeId))
        .fetch();

    if (existingLocations.length > 0) {
        const [existingLocation] = existingLocations;
        preparedLocationsByPlaceId?.set(location.placeId, existingLocation);

        return { existingLocation };
    }

    const locationElement = locationCollection.prepareCreate((loc) => {
        loc.placeId = location.placeId;
        loc.name = location.name;
        loc.lat = `${location.lat}`;
        loc.lng = `${location.lng}`;
        loc.url = location.url;
        loc.utcOffset = location.utcOffset;
        loc.userId = userId;
        loc.organisationId = organisationID;
    });

    preparedLocationsByPlaceId?.set(location.placeId, locationElement);

    return { preparedLocation: locationElement };
};
